<template>
  <div>
     <div class="drawer-header">
           {{  $t('INVOICES') }}
        <div class="header-action">
            <button class="btn header_sbtn" @click="$emit('close')">CANCEL</button>
        <button
          class="btn header_pbtn"
          :disabled="!selectedInvoiceId || isValidating"
          @click="handleAdd">
          {{ isValidating ? 'VALIDATING...' : 'ADD' }}
        </button>
        </div>
        </div>
    <div class="invoice-panel__body">
      <div class="panel-search">
        <svgicon name="search" width="14" height="14" :original="true" />
        <input
          v-model="searchText"
          class="invoice-search-input"
          placeholder="Search invoice"
          @input="onSearchInput" />
      </div>

      <div class="panel-note">
        Note : <span>Invoice shown here are only of returned status.</span>
      </div>

      <div class="invoice-table-header">
        <span class="col-select"></span>
        <span class="col-invoice">{{  $t('INVOICE_NUMBER') }}</span>
        <span class="col-amount">{{  $t('INVOICE_AMOUNT') }}</span>
        <span class="col-due">{{  $t('INVOICE_PAYMENT_DUE_DATE') }}</span>
      </div>

      <div
        v-infinite-scroll="loadMore"
        infinite-scroll-disabled="loadDisabled"
        infinite-scroll-immediate-check="false"
        infinite-scroll-distance="10"
        class="invoice-list">
        <div v-if="isLoading && invoices.length === 0" class="panel-empty">Loading...</div>
        <div v-else-if="!isLoading && invoices.length === 0" class="panel-empty">No invoices found</div>
        <div v-else>
          <Tooltip
            v-for="invoice in invoices"
            :key="invoice.id"
            :disabled="isInvoiceCompatible(invoice)"
            :content="getIncompatibilityReason(invoice) || ''"
            placement="left">
            <div
              class="invoice-row"
              :class="{
                'invoice-row--selected': selectedInvoiceId === invoice.id,
                'invoice-row--disabled': !isInvoiceCompatible(invoice),
              }"
              @click="onRowClick(invoice)">
              <span class="col-select">
                <input
                  type="radio"
                  :value="invoice.id"
                  :disabled="!isInvoiceCompatible(invoice)"
                  v-model="selectedInvoiceId"
                  @click.stop />
              </span>
              <span class="col-invoice">
                <div class="invoice-number">{{ invoice.invoice_number }}</div>
                <div class="invoice-date">{{ formatDate(invoice.date) }}</div>
              </span>
              <span class="col-amount">{{ invoice.amount | indianFormat }}</span>
              <span class="col-due">{{ formatDate(invoice.payment_due_date) }}</span>
            </div>
          </Tooltip>
          <div v-if="isLoading" class="list-loading">Loading more...</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

const RETURNED_STATUS_GROUP = 1003;
const PAGE_LIMIT = 25;

const OUTBOUND_MOVEMENTS = [1, 2]; // SECONDARY = 1, PRIMARY = 2
const INBOUND_MOVEMENTS = [3, 4];  // INWARD = 3, RETURNS = 4

export default {
  props: {
    filterParams: {
      type: Object,
      default: () => ({}),
    },
    selectedPaymentIds: {
      type: Array,
      default: () => [],
    },
    selectedMovement: {
      type: Number,
      default: null,
    },
    selectedDeliveryType: {
      default: null,
    },
    selectedPaymentType: {
      type: Number,
      default: null,
    },
    selectedTruckType: {
      default: null,
    },
    selectedBillToParty: {
      default: null,
    },
  },
  data() {
    return {
      invoices: [],
      selectedInvoiceId: null,
      searchText: '',
      isLoading: false,
      isValidating: false,
      page: 1,
      hasMore: true,
      searchTimer: null,
    };
  },
  computed: {
    loadDisabled() {
      return this.isLoading || !this.hasMore;
    },
    selectedMovementGroup() {
      if (!this.selectedMovement) return null;
      if (OUTBOUND_MOVEMENTS.includes(this.selectedMovement)) return 'outbound';
      if (INBOUND_MOVEMENTS.includes(this.selectedMovement)) return 'inbound';
      return null;
    },
    movementMismatchMessage() {
      if (this.selectedMovementGroup === 'outbound') {
        return 'Primary/Secondary payments cannot be added to a Return/Vendor invoice';
      }
      return 'Return/Vendor payments cannot be added to a Primary/Secondary invoice';
    },
  },
  mounted() {
    this.fetchInvoices(true);
  },
  methods: {
    isMovementCompatible(invoice) {
      if (!this.selectedMovementGroup) return true;
      const isInvoiceOutbound = invoice.is_primary || invoice.is_secondary;
      const isInvoiceInbound = invoice.is_inward || invoice.is_return;
      if (this.selectedMovementGroup === 'outbound') return isInvoiceOutbound;
      if (this.selectedMovementGroup === 'inbound') return isInvoiceInbound;
      return true;
    },
    getIncompatibilityReason(invoice) {
      if (!this.isMovementCompatible(invoice)) return this.movementMismatchMessage;
      if (this.selectedDeliveryType != null && invoice.delivery_type != null
          && this.selectedDeliveryType !== invoice.delivery_type) {
        const sel = this.selectedDeliveryType === 1 ? 'FTL' : 'PTL';
        const inv = invoice.delivery_type === 1 ? 'FTL' : 'PTL';
        return `Cannot mix ${sel} and ${inv} payments on the same invoice`;
      }
      if (this.selectedPaymentType && invoice.type && this.selectedPaymentType !== invoice.type) {
        return 'Payment type mismatch: cannot combine different payment types on the same invoice';
      }
      if (this.selectedTruckType && invoice.truck_type && this.selectedTruckType !== invoice.truck_type) {
        return 'Truck type mismatch: cannot combine different truck types on a Dedicated Truck invoice';
      }
      if (this.selectedBillToParty && invoice.bill_to_party_id && this.selectedBillToParty !== invoice.bill_to_party_id) {
        return 'Bill To Party mismatch: payments must have the same Bill To Party as the invoice';
      }
      return null;
    },
    isInvoiceCompatible(invoice) {
      return this.getIncompatibilityReason(invoice) === null;
    },
    onRowClick(invoice) {
      if (!this.isInvoiceCompatible(invoice)) return;
      this.selectedInvoiceId = invoice.id;
    },
    async fetchInvoices(resetList = false) {
      if (this.isLoading) return;
      if (resetList) {
        this.invoices = [];
        this.page = 1;
        this.hasMore = true;
      }
      this.isLoading = true;
      try {
        const params = {
          ...this.filterParams,
          status_group: [RETURNED_STATUS_GROUP],
          page_number: this.page,
          page_limit: PAGE_LIMIT,
        };
        if (this.searchText && this.searchText.trim()) {
          params.invoice_number = this.searchText.trim();
        }
        const result = await this.$store.dispatch('paymentsV2/fetchReturnedInvoices', params);
        if (result && result.invoiceListData) {
          this.invoices = resetList
            ? result.invoiceListData
            : [...this.invoices, ...result.invoiceListData];
          this.hasMore = result.invoiceListData.length === PAGE_LIMIT;
        } else {
          this.hasMore = false;
        }
      } catch (error) {
        this.hasMore = false;
      } finally {
        this.isLoading = false;
      }
    },
    loadMore() {
      if (!this.hasMore || this.isLoading) return;
      this.page += 1;
      this.fetchInvoices(false);
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.fetchInvoices(true);
      }, 400);
    },
    async handleAdd() {
      if (!this.selectedInvoiceId) return;

      const selectedInvoice = this.invoices.find(i => i.id === this.selectedInvoiceId);
      if (selectedInvoice && !this.isMovementCompatible(selectedInvoice)) {
        this.$errorMessage({ message: this.movementMismatchMessage });
        return;
      }

      this.isValidating = true;
      try {
        const eligibleResult = await this.$store.dispatch('paymentsV2/getEligiblePayments', {
          invoiceId: this.selectedInvoiceId,
          params: { page_limit: 1000, page_number: 1 },
        });
        if (!eligibleResult || eligibleResult.error) {
          this.$errorMessage({ message: 'Unable to validate payments. Please try again.' });
          return;
        }
        const eligibleIds = new Set((eligibleResult.payments || []).map(p => p.id));
        const ineligible = this.selectedPaymentIds.filter(id => !eligibleIds.has(id));
        if (ineligible.length > 0) {
          this.$errorMessage({
            message: `${ineligible.length} selected payment(s) cannot be added to this invoice.`,
          });
          return;
        }
        this.$emit('addToInvoice', {
          invoiceId: this.selectedInvoiceId,
          paymentIds: this.selectedPaymentIds,
        });
      } finally {
        this.isValidating = false;
      }
    },
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD MMM YYYY');
    },
  },
  filters: {
    indianFormat(value) {
      if (value == null) return '-';
      return Number(value).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
.invoice-panel__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: hidden;
  padding: 1px 10px 1px 20px;
}

.panel-search {
  display: flex;
  align-items: center;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  padding: 6px 10px;
  margin-bottom: 10px;
  gap: 8px;
  background: #fff;
}

.invoice-search-input {
  border: none;
  outline: none;
  font-size: 1.2rem;
  width: 100%;
}

.panel-note {
  font-size: 1.1rem;
  color: #555;
  margin-bottom: 10px;
}

.panel-note span {
  color: #333;
}

.invoice-table-header {
  display: flex;
  align-items: center;
  font-size: 1.1rem;
  font-weight: 700;
  padding: 6px 0;
  border-bottom: 1px solid #e0e0e0;
  letter-spacing: 0.04em;
}

.invoice-list {
  flex: 1;
  overflow-y: auto;
}

.invoice-row {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  font-size: 1.2rem;
}

.invoice-row:hover:not(.invoice-row--disabled) {
  background: #f5f9ff;
}

.invoice-row--selected {
  background: #edf4ff;
}

.invoice-row--disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.col-select { width: 28px; flex-shrink: 0; }
.col-invoice { flex: 1.5; }
.col-amount { flex: 1; text-align: right; padding-right: 8px; }
.col-due { flex: 1.2; text-align: right; }

.invoice-number { font-weight: 500; color: #1a2b4a; }
.invoice-date { font-size: 1rem; color: #888; margin-top: 2px; }

.list-loading {
  text-align: center;
  color: #999;
  padding: 10px 0;
  font-size: 1.2rem;
}

.panel-empty {
  text-align: center;
  color: #999;
  padding: 32px 0;
  font-size: 1.2rem;
}
</style>
